/*
* Order matrix pricing helpers
*/

import * as _ from 'lodash';

import * as AppUtilities from '../common/apputilities';
import { Formatters } from '../common/formatters';

export interface IPriceMatrixDocument {
    _id?: string;
    economyPrice?: number;
    level: string;
    rushPrice?: number;
    standardPrice?: number;
}

export class OrderStatuses {
    public static ANY_STATUS: number = -1;
    public static BID_SENT_LIMBO: number = 3;
    public static JOB_WON: number = -99;
    public static JOB_SENT_OR_WON_STATUSES: number = -99;
    public static SITE_INSPECTION_PENDING: number = 6;
    public static ORDER_3_YEAR_PLANS_STATUSES: number = -88;
    public static ORDER_3_YEAR_PENDING_START: number = 116; // 115;
    public static ORDER_3_YEAR_RENEWAL: number = 115; // 116;
    public static WAITING_ON_WORKSHEET: number = 114; // added 08/07/2024 per Rob's request
}

export class OrderServiceLevels {
    public static ORDER_3_YEAR_PLANS_SERVICE_LEVELS: number = -88;
}

export class OrderPricing {
    public static readonly OrderServicesPlanMaxSequence: number = 3;
    public static readonly OrderServicesPricingMatrixFields = {
        economyPricing: 'economyPricing',
        standardPricing: 'standardPricing',
        rushPricing: 'rushPricing',
    };

    public static readonly MatrixItemFields = {
        economy: 'economyPrice',
        level: 'level',
        rush: 'rushPrice',
        standard: 'standardPrice',
    };

    public static readonly ServiceLevelConfig = {
        economy: {
            level1: { serviceLevelId: 5, },
            level2: { serviceLevelId: 6, },
            level3: { serviceLevelId: 7, },
            level4: { serviceLevelId: 122, }, // 122
            level4_2: { serviceLevelId: 121, }, // 121
            level4_3: { serviceLevelId: 120, }, // 120
            // level5: { serviceLevelId: 8, },
        },
        standard: {
            level1: { serviceLevelId: 1, },
            level2: { serviceLevelId: 2, },
            level3: { serviceLevelId: 3, },
            level4: { serviceLevelId: 116, }, // 115, }, // 116,
            level4_2: { serviceLevelId: 124, }, // 124
            level4_3: { serviceLevelId: 123, }, // 123
            // level5: { serviceLevelId: 4, },
        },
        rush: {
            level1: { serviceLevelId: 9, },
            level2: { serviceLevelId: 10, },
            level3: { serviceLevelId: 11, },
            level4: { serviceLevelId: 119, }, // 119
            level4_2: { serviceLevelId: 118, }, // 118
            level4_3: { serviceLevelId: 117, }, // 117
            // level5: { serviceLevelId: 12, },
        },
        // level4: {
        //     level5: { serviceLevelId: 13 },
        // },
        other: {
            level1: { serviceLevelId: 14 },
        },
        threeYearPlan: {
            level5: { serviceLevelId: 15 },
        },
        // test: {
        //     standardl42: { serviceLevelId: 124 }
        // }
    };

    public static readonly PricingLevelConfig = [
        { level: '4', readOnly: true }
    ];

    public static readonly PricingLevelConfigDefaults = {
        NumberToRound: 25,
    };

    // TODO: CAN SETPRICESCONFIG BE REFACTORED OUT SOMEWHERE ELSE (/CONFIG) ?
    public static SetPricesConfig: Array<any> = [
        /* LEVEL 1 PRICING LEVEL CONFIG */
        {
            target: { level: '1', priceField: OrderPricing.MatrixItemFields.economy },
            sources: [
                // target is source
                { level: '2', priceField: OrderPricing.MatrixItemFields.economy, flatAmount: 0, multipliers: [1.25], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateSimplePrice },
                { level: '3', priceField: OrderPricing.MatrixItemFields.economy, flatAmount: 0, multipliers: [2.50], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateSimplePrice },
            ]
        },
        {
            target: { level: '1', priceField: OrderPricing.MatrixItemFields.standard },
            sources: [
                { level: '1', priceField: OrderPricing.MatrixItemFields.economy, flatAmount: 0, multipliers: [1.25], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateSimplePrice },
                { level: '2', priceField: OrderPricing.MatrixItemFields.economy, flatAmount: 0, multipliers: [1.5625], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateSimplePrice },
                { level: '3', priceField: OrderPricing.MatrixItemFields.economy, flatAmount: 0, multipliers: [3.125], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateSimplePrice },
            ]
        },
        {
            target: { level: '1', priceField: OrderPricing.MatrixItemFields.rush },
            sources: [
                { level: '1', priceField: OrderPricing.MatrixItemFields.standard, flatAmount: 0, multipliers: [1.35], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateSimplePrice },
                { level: '2', priceField: OrderPricing.MatrixItemFields.economy, flatAmount: 0, multipliers: [2.109375], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateSimplePrice },
                { level: '3', priceField: OrderPricing.MatrixItemFields.economy, flatAmount: 0, multipliers: [4.21875], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateSimplePrice },
            ]
        },
        /* LEVEL 2 PRICING LEVEL CONFIG */
        {
            target: { level: '2', priceField: OrderPricing.MatrixItemFields.economy },
            sources: [
                { level: '1', priceField: OrderPricing.MatrixItemFields.economy, flatAmount: 0, multipliers: [0.8], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateSimplePrice },
                // target is source
                { level: '3', priceField: OrderPricing.MatrixItemFields.economy, flatAmount: 0, multipliers: [2], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateSimplePrice },
            ]
        },
        {
            target: { level: '2', priceField: OrderPricing.MatrixItemFields.standard },
            sources: [
                { level: '1', priceField: OrderPricing.MatrixItemFields.standard, flatAmount: 0, multipliers: [0.8], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateSimplePrice },
                { level: '2', priceField: OrderPricing.MatrixItemFields.economy, flatAmount: 0, multipliers: [1.25], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateSimplePrice },
                { level: '3', priceField: OrderPricing.MatrixItemFields.economy, flatAmount: 0, multipliers: [2.50], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateSimplePrice },
            ]
        },
        {
            target: { level: '2', priceField: OrderPricing.MatrixItemFields.rush },
            sources: [
                { level: '1', priceField: OrderPricing.MatrixItemFields.rush, flatAmount: 0, multipliers: [0.8], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateSimplePrice },
                { level: '2', priceField: OrderPricing.MatrixItemFields.standard, flatAmount: 0, multipliers: [1.35], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateSimplePrice },
                { level: '3', priceField: OrderPricing.MatrixItemFields.economy, flatAmount: 0, multipliers: [3.375], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateSimplePrice },
            ]
        },
        /* LEVEL 3 PRICING LEVEL CONFIG */
        {
            target: { level: '3', priceField: OrderPricing.MatrixItemFields.economy },
            sources: [
                { level: '1', priceField: OrderPricing.MatrixItemFields.economy, flatAmount: 0, multipliers: [0.4], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateSimplePrice },
                { level: '2', priceField: OrderPricing.MatrixItemFields.standard, flatAmount: 0, multipliers: [0.4], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateSimplePrice },
                // target is source
            ]
        },
        {
            target: { level: '3', priceField: OrderPricing.MatrixItemFields.standard },
            sources: [
                { level: '1', priceField: OrderPricing.MatrixItemFields.standard, flatAmount: 0, multipliers: [0.4], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateSimplePrice },
                { level: '2', priceField: OrderPricing.MatrixItemFields.standard, flatAmount: 0, multipliers: [0.50], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateSimplePrice },
                { level: '3', priceField: OrderPricing.MatrixItemFields.economy, flatAmount: 0, multipliers: [1.25], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateSimplePrice },
            ]
        },
        {
            target: { level: '3', priceField: OrderPricing.MatrixItemFields.rush },
            sources: [
                { level: '1', priceField: OrderPricing.MatrixItemFields.rush, flatAmount: 0, multipliers: [0.4], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateSimplePrice },
                { level: '2', priceField: OrderPricing.MatrixItemFields.rush, flatAmount: 0, multipliers: [0.5], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateSimplePrice },
                { level: '3', priceField: OrderPricing.MatrixItemFields.standard, flatAmount: 0, multipliers: [1.35], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateSimplePrice },
            ]
        },
        /* LEVEL 4 (Duplicate of Level 5, for historic data consistency purposes) PRICING LEVEL CONFIG */
        {
            target: { level: '4', priceField: OrderPricing.MatrixItemFields.economy },
            sources: [
                { level: '1', priceField: OrderPricing.MatrixItemFields.economy, flatAmount: 0, multipliers: [0.4, 2, 0.8, 3], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateComplexPrice },
                { level: '2', priceField: OrderPricing.MatrixItemFields.economy, flatAmount: 0, multipliers: [0.65, 2, 0.8, 3], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateComplexPrice },
                { level: '3', priceField: OrderPricing.MatrixItemFields.economy, flatAmount: 0, multipliers: [1.8, 2, 0.8, 3], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateComplexPrice },
            ]
        },
        {
            target: { level: '4', priceField: OrderPricing.MatrixItemFields.standard },
            sources: [
                { level: '1', priceField: OrderPricing.MatrixItemFields.standard, flatAmount: 0, multipliers: [0.4, 2, 0.8, 3], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateComplexPrice },
                { level: '2', priceField: OrderPricing.MatrixItemFields.standard, flatAmount: 0, multipliers: [0.65, 2, 0.8, 3], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateComplexPrice },
                { level: '3', priceField: OrderPricing.MatrixItemFields.standard, flatAmount: 0, multipliers: [1.8, 2, 0.8, 3], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateComplexPrice },
                // { level: '4', priceField: OrderPricing.MatrixItemFields.standard, flatAmount: 0, multipliers: [1.8, 2, 0.8, 3], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateComplexPrice },
            ]
        },
        {
            target: { level: '4', priceField: OrderPricing.MatrixItemFields.rush },
            sources: [
                { level: '1', priceField: OrderPricing.MatrixItemFields.rush, flatAmount: 0, multipliers: [0.4, 2, 0.8, 3], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateComplexPrice },
                { level: '2', priceField: OrderPricing.MatrixItemFields.rush, flatAmount: 0, multipliers: [0.65, 2, 0.8, 3], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateComplexPrice },
                { level: '3', priceField: OrderPricing.MatrixItemFields.rush, flatAmount: 0, multipliers: [1.8, 2, 0.8, 3], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateComplexPrice },
                // { level: '4', priceField: OrderPricing.MatrixItemFields.rush, flatAmount: 0, multipliers: [1.8, 2, 0.8, 3], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateComplexPrice },
            ],
        },
        /* LEVEL 5 (Displayed as "3-Year Plan"/"Level 4" pricing) PRICING LEVEL CONFIG */
        {
            target: { level: '5', priceField: OrderPricing.MatrixItemFields.economy },
            sources: [
                { level: '1', priceField: OrderPricing.MatrixItemFields.economy, flatAmount: 0, multipliers: [0.4, 2, 0.8, 3], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateComplexPrice },
                { level: '2', priceField: OrderPricing.MatrixItemFields.economy, flatAmount: 0, multipliers: [0.65, 2, 0.8, 3], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateComplexPrice },
                { level: '3', priceField: OrderPricing.MatrixItemFields.economy, flatAmount: 0, multipliers: [1.8, 2, 0.8, 3], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateComplexPrice },
            ]
        },
        {
            target: { level: '5', priceField: OrderPricing.MatrixItemFields.standard },
            sources: [
                { level: '1', priceField: OrderPricing.MatrixItemFields.standard, flatAmount: 0, multipliers: [0.4, 2, 0.8, 3], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateComplexPrice },
                { level: '2', priceField: OrderPricing.MatrixItemFields.standard, flatAmount: 0, multipliers: [0.65, 2, 0.8, 3], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateComplexPrice },
                { level: '3', priceField: OrderPricing.MatrixItemFields.standard, flatAmount: 0, multipliers: [1.8, 2, 0.8, 3], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateComplexPrice },
                // { level: '4', priceField: OrderPricing.MatrixItemFields.standard, flatAmount: 0, multipliers: [1.8, 2, 0.8, 3], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateComplexPrice },
            ]
        },
        {
            target: { level: '5', priceField: OrderPricing.MatrixItemFields.rush },
            sources: [
                { level: '1', priceField: OrderPricing.MatrixItemFields.rush, flatAmount: 0, multipliers: [0.4, 2, 0.8, 3], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateComplexPrice },
                { level: '2', priceField: OrderPricing.MatrixItemFields.rush, flatAmount: 0, multipliers: [0.65, 2, 0.8, 3], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateComplexPrice },
                { level: '3', priceField: OrderPricing.MatrixItemFields.rush, flatAmount: 0, multipliers: [1.8, 2, 0.8, 3], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateComplexPrice },
                // { level: '4', priceField: OrderPricing.MatrixItemFields.rush, flatAmount: 0, multipliers: [1.8, 2, 0.8, 3], numberToRound: OrderPricing.PricingLevelConfigDefaults.NumberToRound, calculate: OrderPricing.CalculateComplexPrice },
            ],
        },
    ];

    public static StatusUpdateFieldsConfig: Array<any> = [
        {
            // Bid Sent/Limbo
            statusId: 3,
            updateDateFields: [
                { fieldKey: 'bidSentDate', addDays: 0, updateExisting: false },
            ],
        },

        // SET WONDATE
        {
            // Pending Start            
            statusId: 5,
            updateDateFields: [
                { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
            ],
        },
        {
            // SI Pending
            statusId: 6,
            updateDateFields: [
                { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
            ],
        },
        {
            // Insp. Complete
            statusId: 7,
            updateDateFields: [
                { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
            ],
        },
        {
            // Entry 1       
            statusId: 8,
            updateDateFields: [
                { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
            ],
        },
        {
            // Entry 2
            statusId: 9,
            updateDateFields: [
                { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
            ],
        },
        {
            // Entry 3
            statusId: 10,
            updateDateFields: [
                { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
            ],
        },
        {
            // Researching
            statusId: 11,
            updateDateFields: [
                { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
            ],
        },
        {
            // Client Questions
            statusId: 12,
            updateDateFields: [
                { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
            ],
        },
        {
            // Finalizing
            statusId: 13,
            updateDateFields: [
                { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
            ],
        },

        {
            // Bid Sent
            statusId: 14,
            updateDateFields: [
                { fieldKey: 'bidSentDate', addDays: 0, updateExisting: false },
                { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
            ],
        },
    ];

    public static ServiceLevelEconomyConfig: Array<any> = [
        // ECONOMY LEVELS
        {
            // Economy Level 1
            serviceLevelId: OrderPricing.ServiceLevelConfig.economy.level1.serviceLevelId, // 5,
            updateAcceptedPrice: { update: true, pricingLevel: 1, priceFieldKey: OrderPricing.MatrixItemFields.economy },
            updateDateFields: [
                { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
                { fieldKey: 'startDate', addDays: 0, updateExisting: false },
                { fieldKey: 'dueDate', addDays: 49, updateExisting: false },
            ],
            updateStatusFields: [
                { fieldKey: 'status.statusId', fieldValue: 6, updateExisting: true },
            ]
        },
        {
            // Economy Level 2
            serviceLevelId: OrderPricing.ServiceLevelConfig.economy.level2.serviceLevelId, // 6,
            updateAcceptedPrice: { update: true, pricingLevel: 2, priceFieldKey: OrderPricing.MatrixItemFields.economy },
            updateDateFields: [
                { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
                { fieldKey: 'startDate', addDays: 0, updateExisting: false },
                { fieldKey: 'dueDate', addDays: 49, updateExisting: false },
            ],
            updateStatusFields: [
                { fieldKey: 'status.statusId', fieldValue: 6, updateExisting: true },
            ]
        },
        {
            // Economy Level 3
            serviceLevelId: OrderPricing.ServiceLevelConfig.economy.level3.serviceLevelId, // 7,
            updateAcceptedPrice: { update: true, pricingLevel: 3, priceFieldKey: OrderPricing.MatrixItemFields.economy },
            updateDateFields: [
                { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
                { fieldKey: 'startDate', addDays: 0, updateExisting: false },
                { fieldKey: 'dueDate', addDays: 49, updateExisting: false },
            ],
            updateStatusFields: [
                { fieldKey: 'status.statusId', fieldValue: 6, updateExisting: true },
            ]
        },
        {
            // Economy Level 4 (L4-1)
            serviceLevelId: OrderPricing.ServiceLevelConfig.economy.level4_2.serviceLevelId, // 122,
            updateAcceptedPrice: { update: true, pricingLevel: 4, priceFieldKey: OrderPricing.MatrixItemFields.economy },
            updateDateFields: [
                { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
                { fieldKey: 'startDate', addDays: 0, updateExisting: false },
                { fieldKey: 'dueDate', addDays: 49, updateExisting: false },
            ],
            updateStatusFields: [
                { fieldKey: 'status.statusId', fieldValue: 6, updateExisting: true },
            ]
        },
        {
            // Economy Level 4 (L4-2)
            serviceLevelId: OrderPricing.ServiceLevelConfig.economy.level4_3.serviceLevelId, // 121,
            updateAcceptedPrice: { update: true, pricingLevel: 4, priceFieldKey: OrderPricing.MatrixItemFields.economy },
            updateDateFields: [
                { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
                { fieldKey: 'startDate', addDays: 0, updateExisting: false },
                { fieldKey: 'dueDate', addDays: 49, updateExisting: false },
            ],
            updateStatusFields: [
                { fieldKey: 'status.statusId', fieldValue: 6, updateExisting: true },
            ]
        },
        {
            // Economy Level 4 (L4-3)
            serviceLevelId: OrderPricing.ServiceLevelConfig.economy.level4.serviceLevelId, // 120,
            updateAcceptedPrice: { update: true, pricingLevel: 4, priceFieldKey: OrderPricing.MatrixItemFields.economy },
            updateDateFields: [
                { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
                { fieldKey: 'startDate', addDays: 0, updateExisting: false },
                { fieldKey: 'dueDate', addDays: 49, updateExisting: false },
            ],
            updateStatusFields: [
                { fieldKey: 'status.statusId', fieldValue: 6, updateExisting: true },
            ]
        },
        /*{
            // Economy Level 5
            serviceLevelId: OrderPricing.ServiceLevelConfig.economy.level5.serviceLevelId, // 8,
            updateAcceptedPrice: { update: true, pricingLevel: 5, priceFieldKey: OrderPricing.MatrixItemFields.economy },
            updateDateFields: [
                { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
                { fieldKey: 'startDate', addDays: 0, updateExisting: false },
                { fieldKey: 'dueDate', addDays: 49, updateExisting: false },
            ],
            updateStatusFields: [
                { fieldKey: 'status.statusId', fieldValue: 6, updateExisting: true },
            ]
        },*/
    ];

    public static ServiceLevelStandardConfig: Array<any> = [
        // STANDARD LEVELS
        {
            // Standard Level 1
            serviceLevelId: OrderPricing.ServiceLevelConfig.standard.level1.serviceLevelId, // 1,
            updateAcceptedPrice: { update: true, pricingLevel: 1, priceFieldKey: OrderPricing.MatrixItemFields.standard },
            updateDateFields: [
                { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
                { fieldKey: 'startDate', addDays: 0, updateExisting: false },
                { fieldKey: 'dueDate', addDays: 35, updateExisting: false },
            ],
            updateStatusFields: [
                { fieldKey: 'status.statusId', fieldValue: 6, updateExisting: true },
            ]
        },
        {
            // Standard Level 2
            serviceLevelId: OrderPricing.ServiceLevelConfig.standard.level2.serviceLevelId, // 2,
            updateAcceptedPrice: { update: true, pricingLevel: 2, priceFieldKey: OrderPricing.MatrixItemFields.standard },
            updateDateFields: [
                { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
                { fieldKey: 'startDate', addDays: 0, updateExisting: false },
                { fieldKey: 'dueDate', addDays: 35, updateExisting: false },
            ],
            updateStatusFields: [
                { fieldKey: 'status.statusId', fieldValue: 6, updateExisting: true },
            ]
        },
        {
            // Standard Level 3
            serviceLevelId: OrderPricing.ServiceLevelConfig.standard.level3.serviceLevelId, // 3,
            updateAcceptedPrice: { update: true, pricingLevel: 3, priceFieldKey: OrderPricing.MatrixItemFields.standard },
            updateDateFields: [
                { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
                { fieldKey: 'startDate', addDays: 0, updateExisting: false },
                { fieldKey: 'dueDate', addDays: 35, updateExisting: false },
            ],
            updateStatusFields: [
                { fieldKey: 'status.statusId', fieldValue: 6, updateExisting: true },
            ]
        },
        {
            // Standard Level 4
            serviceLevelId: OrderPricing.ServiceLevelConfig.standard.level4.serviceLevelId, // 116,
            updateAcceptedPrice: { update: true, pricingLevel: 4, priceFieldKey: OrderPricing.MatrixItemFields.standard },
            updateDateFields: [
                { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
                { fieldKey: 'startDate', addDays: 0, updateExisting: false },
                { fieldKey: 'dueDate', addDays: 35, updateExisting: false },
            ],
            updateStatusFields: [
                { fieldKey: 'status.statusId', fieldValue: 6, updateExisting: true },
            ]
        },
        {
            // Standard Level 4
            serviceLevelId: OrderPricing.ServiceLevelConfig.standard.level4_2.serviceLevelId, // 124,
            updateAcceptedPrice: { update: true, pricingLevel: 4, priceFieldKey: OrderPricing.MatrixItemFields.standard },
            updateDateFields: [
                { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
                { fieldKey: 'startDate', addDays: 0, updateExisting: false },
                { fieldKey: 'dueDate', addDays: 35, updateExisting: false },
            ],
            updateStatusFields: [
                { fieldKey: 'status.statusId', fieldValue: 6, updateExisting: true },
            ]
        },
        {
            // Standard Level 4
            serviceLevelId: OrderPricing.ServiceLevelConfig.standard.level4_3.serviceLevelId, // 123,
            updateAcceptedPrice: { update: true, pricingLevel: 4, priceFieldKey: OrderPricing.MatrixItemFields.standard },
            updateDateFields: [
                { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
                { fieldKey: 'startDate', addDays: 0, updateExisting: false },
                { fieldKey: 'dueDate', addDays: 35, updateExisting: false },
            ],
            updateStatusFields: [
                { fieldKey: 'status.statusId', fieldValue: 6, updateExisting: true },
            ]
        },
        /*{
            // Standard Level 5
            serviceLevelId: OrderPricing.ServiceLevelConfig.standard.level5.serviceLevelId, // 4,
            updateAcceptedPrice: { update: true, pricingLevel: 5, priceFieldKey: OrderPricing.MatrixItemFields.standard },
            updateDateFields: [
                { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
                { fieldKey: 'startDate', addDays: 0, updateExisting: false },
                { fieldKey: 'dueDate', addDays: 35, updateExisting: false },
            ],
            updateStatusFields: [
                { fieldKey: 'status.statusId', fieldValue: 6, updateExisting: true },
            ]
        },*/
    ];

    public static ServiceLevelRushConfig: Array<any> = [
        // RUSH LEVELS
        {
            // Rush Level 1
            serviceLevelId: OrderPricing.ServiceLevelConfig.rush.level1.serviceLevelId, // 9,
            updateAcceptedPrice: { update: true, pricingLevel: 1, priceFieldKey: OrderPricing.MatrixItemFields.rush },
            updateDateFields: [
                { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
                { fieldKey: 'startDate', addDays: 0, updateExisting: false },
                { fieldKey: 'dueDate', addDays: 14, updateExisting: false },
            ],
            updateStatusFields: [
                { fieldKey: 'status.statusId', fieldValue: 6, updateExisting: true },
            ]
        },
        {
            // Rush Level 2
            serviceLevelId: OrderPricing.ServiceLevelConfig.rush.level2.serviceLevelId, // 10,
            updateAcceptedPrice: { update: true, pricingLevel: 2, priceFieldKey: OrderPricing.MatrixItemFields.rush },
            updateDateFields: [
                { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
                { fieldKey: 'startDate', addDays: 0, updateExisting: false },
                { fieldKey: 'dueDate', addDays: 14, updateExisting: false },
            ],
            updateStatusFields: [
                { fieldKey: 'status.statusId', fieldValue: 6, updateExisting: true },
            ]
        },
        {
            // Rush Level 3
            serviceLevelId: OrderPricing.ServiceLevelConfig.rush.level3.serviceLevelId, // 11,
            updateAcceptedPrice: { update: true, pricingLevel: 3, priceFieldKey: OrderPricing.MatrixItemFields.rush },
            updateDateFields: [
                { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
                { fieldKey: 'startDate', addDays: 0, updateExisting: false },
                { fieldKey: 'dueDate', addDays: 14, updateExisting: false },
            ],
            updateStatusFields: [
                { fieldKey: 'status.statusId', fieldValue: 6, updateExisting: true },
            ]
        },
        {
            // Rush Level 4
            serviceLevelId: OrderPricing.ServiceLevelConfig.rush.level4.serviceLevelId, // 119,
            updateAcceptedPrice: { update: true, pricingLevel: 4, priceFieldKey: OrderPricing.MatrixItemFields.rush },
            updateDateFields: [
                { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
                { fieldKey: 'startDate', addDays: 0, updateExisting: false },
                { fieldKey: 'dueDate', addDays: 14, updateExisting: false },
            ],
            updateStatusFields: [
                { fieldKey: 'status.statusId', fieldValue: 6, updateExisting: true },
            ]
        },
        {
            // Rush Level 4
            serviceLevelId: OrderPricing.ServiceLevelConfig.rush.level4_2.serviceLevelId, // 118,
            updateAcceptedPrice: { update: true, pricingLevel: 4, priceFieldKey: OrderPricing.MatrixItemFields.rush },
            updateDateFields: [
                { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
                { fieldKey: 'startDate', addDays: 0, updateExisting: false },
                { fieldKey: 'dueDate', addDays: 14, updateExisting: false },
            ],
            updateStatusFields: [
                { fieldKey: 'status.statusId', fieldValue: 6, updateExisting: true },
            ]
        },
        {
            // Rush Level 4
            serviceLevelId: OrderPricing.ServiceLevelConfig.rush.level4_3.serviceLevelId, // 117,
            updateAcceptedPrice: { update: true, pricingLevel: 4, priceFieldKey: OrderPricing.MatrixItemFields.rush },
            updateDateFields: [
                { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
                { fieldKey: 'startDate', addDays: 0, updateExisting: false },
                { fieldKey: 'dueDate', addDays: 14, updateExisting: false },
            ],
            updateStatusFields: [
                { fieldKey: 'status.statusId', fieldValue: 6, updateExisting: true },
            ]
        },
        /*{
            // Rush Level 5
            serviceLevelId: OrderPricing.ServiceLevelConfig.rush.level5.serviceLevelId, // 12,
            updateAcceptedPrice: { update: true, pricingLevel: 5, priceFieldKey: OrderPricing.MatrixItemFields.rush },
            updateDateFields: [
                { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
                { fieldKey: 'startDate', addDays: 0, updateExisting: false },
                { fieldKey: 'dueDate', addDays: 14, updateExisting: false },
            ],
            updateStatusFields: [
                { fieldKey: 'status.statusId', fieldValue: 6, updateExisting: true },
            ]
        },*/
    ];

    public static ServiceLevelUpdateFieldsConfig: Array<any> = _.concat(
        OrderPricing.ServiceLevelStandardConfig,
        OrderPricing.ServiceLevelEconomyConfig,
        OrderPricing.ServiceLevelRushConfig,
        [
            // STANDARD L4-2
            /*{
                serviceLevelId: OrderPricing.ServiceLevelConfig.test.standardl42.serviceLevelId, // 124
                updateAcceptedPrice: { update: true, pricingLevel: 5, priceFieldKey: OrderPricing.MatrixItemFields.standard },
                updateDateFields: [
                    { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
                    { fieldKey: 'startDate', addDays: 0, updateExisting: false },
                    // { fieldKey: 'dueDate', addDays: 49, updateExisting: false },
                ],
                updateStatusFields: [
                    { fieldKey: 'status.statusId', fieldValue: 6, updateExisting: false },
                ]
            },*/

            // STANDARD LEVEL 4 LEVELS
            /*{
                // Level 4 Standard
                serviceLevelId: OrderPricing.ServiceLevelConfig.level4.level5.serviceLevelId, // 13,
                updateAcceptedPrice: { update: true, pricingLevel: 5, priceFieldKey: OrderPricing.MatrixItemFields.economy },
                updateDateFields: [
                    { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
                    { fieldKey: 'startDate', addDays: 0, updateExisting: false },
                    // { fieldKey: 'dueDate', addDays: 49, updateExisting: false },
                ],
                updateStatusFields: [
                    { fieldKey: 'status.statusId', fieldValue: 6, updateExisting: true },
                ]
            },*/

            {
                // Other
                serviceLevelId: OrderPricing.ServiceLevelConfig.other.level1.serviceLevelId,  // 14,
                updateAcceptedPrice: { update: false, pricingLevel: 1, priceFieldKey: OrderPricing.MatrixItemFields.standard },
                updateDateFields: [
                    { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
                    { fieldKey: 'startDate', addDays: 0, updateExisting: false },
                ],
                updateStatusFields: [
                    { fieldKey: 'status.statusId', fieldValue: 6, updateExisting: true },
                ]
            },

            {
                // 3 Year Plan
                serviceLevelId: OrderPricing.ServiceLevelConfig.threeYearPlan.level5.serviceLevelId, // 15,
                updateAcceptedPrice: { update: true, pricingLevel: 5, priceFieldKey: OrderPricing.MatrixItemFields.economy },
                updateDateFields: [
                    { fieldKey: 'wonDate', addDays: 0, updateExisting: false },
                    { fieldKey: 'startDate', addDays: 0, updateExisting: false },
                    // { fieldKey: 'dueDate', addDays: 49, updateExisting: false },
                ],
                updateStatusFields: [
                    { fieldKey: 'status.statusId', fieldValue: 6, updateExisting: true },
                ]
            },
        ]);

    public static CalculateSimplePrice(sourcePrice: number, multipliers: Array<number>, allSources: any = []) {
        // Returns calculated price via simple multiplication
        if (!AppUtilities.isArrayWithLength(multipliers)) { return sourcePrice; }

        return sourcePrice * multipliers[0];
    }

    public static CalculateComplexPrice(sourcePrice: number, multipliers: Array<number>, allSources: any = []) {
        // Returns calculated price via complex method (level 5 pricing matrix)
        if (!AppUtilities.isArrayWithLength(multipliers)) { return sourcePrice; }

        if (multipliers.length !== 4) { return sourcePrice; }

        return ((sourcePrice + ((sourcePrice * multipliers[0]) * multipliers[1])) * multipliers[2]) / multipliers[3];
    }

    public static CalculateComplexCompPrice(sourcePrice: number, multipliers: Array<number>, comp: any = {}) {
        console.log(`CalculateComplexCompPrice`, { comp });

        return 0;

        // // Returns calculated price via 3 year plan method (level 5 pricing matrix)
        // if (!AppUtilities.isArrayWithLength(allSources)) { return sourcePrice; }

        // // Level 5 Standard pricing: (Level1Standard - Level1Economy) + Level5Economy
        // const lev1Standard = this.getOrderPricingLevel1Economy

        // if (multipliers.length !== 4) { return sourcePrice; }

        // return ((sourcePrice + ((sourcePrice * multipliers[0]) * multipliers[1])) * multipliers[2]) / multipliers[3];
    }

    public static buildPriceMatrixAndSetPrices(priceMatrix: Array<any> = [], sourceLevel: string = '1') {
        if (!AppUtilities.isArrayWithLength(priceMatrix)) { return []; }

        let newMatrix: any = _.cloneDeep(priceMatrix);

        OrderPricing.SetPricesConfig.forEach(c => {
            const cSources = _.get(c, 'sources', []);

            const cSource = _.find(cSources, ['level', sourceLevel]);
            const cTarget = _.get(c, 'target');

            if (!cSource || !cTarget) { return; }

            // const cCompSource = _.find(cSources, ['level', sourceLevel]);

            // const msg = `SetPricesConfig.forEach: ${cTarget.level}-${cTarget.priceField} sourceLevel ${sourceLevel}`;
            // console.log(`${msg}`, { cSource, c, cSources, cTarget });

            let sourcePrice: number = Formatters.getFloat(cSource, 'flatAmount');
            if (sourceLevel && Formatters.getStr(cSource, 'priceField')) {
                sourcePrice = OrderPricing.getOrderLevelPricing(newMatrix, Number(sourceLevel), cSource.priceField);
            }

            let targetItem: any = OrderPricing.getOrderPricingMatrixItem(newMatrix, cTarget.level);

            const calculatedPrice = cSource.calculate(sourcePrice, cSource.multipliers);

            const roundedPrice = Formatters.getCustomRoundToMultiple(calculatedPrice, cSource.numberToRound);

            // console.log(`${msg} sourcePrice ${sourcePrice} calculatedPrice ${calculatedPrice} roundedPrice ${roundedPrice} sourceLevel ${sourceLevel} cSource.level ${cSource.level}`, { c, cSource, cTarget });

            _.set(targetItem, `${cTarget.priceField}`, roundedPrice);
        });

        const level5EcoPrice = newMatrix[4].economyPrice;
        const level1EcoPrice = newMatrix[0].economyPrice;
        const level1StandPrice = newMatrix[0].standardPrice;
        const level1RushPrice = newMatrix[0].rushPrice;
        const level1EcoStandDiff = level1StandPrice - level1EcoPrice;
        const level1EcoRushDiff = level1RushPrice - level1EcoPrice;

        _.set(newMatrix[3], 'standardPrice', level5EcoPrice + level1EcoStandDiff);
        _.set(newMatrix[4], 'standardPrice', level5EcoPrice + level1EcoStandDiff);
        _.set(newMatrix[3], 'rushPrice', level5EcoPrice + level1EcoRushDiff);
        _.set(newMatrix[4], 'rushPrice', level5EcoPrice + level1EcoRushDiff);

        return newMatrix;
    }

    /* Order price matrix helper functions */
    public static getOrderPricingMatrixItem(pricingMatrix: Array<any>, pricingLevel: number) {
        // Returns a specific order pricing matrix entry for a given level

        if (!AppUtilities.isArrayWithLength(pricingMatrix) || !pricingLevel) { return; }

        return _.find(pricingMatrix, [OrderPricing.MatrixItemFields.level, pricingLevel.toString()]);
    }

    public static getOrderLevelPricing(pricingMatrix: Array<any>, pricingLevel: number, priceFieldKey: string) {
        // Returns a specific order pricing matrix entry for a given level & key
        // Example: pricingLevel = 1, priceFieldKey = OrderPricing.MatrixItemFields.economy

        if (!AppUtilities.isArrayWithLength(pricingMatrix) || !pricingLevel || !priceFieldKey) { return 0; }

        const levelPricing = OrderPricing.getOrderPricingMatrixItem(pricingMatrix, pricingLevel);

        if (levelPricing) {
            return Formatters.getFloat(levelPricing, priceFieldKey);
        } else {
            return 0;
        }
    }

    public static getOrderPricingLevel1Economy(pricingMatrix: Array<any>, priceLevel: number = 1, priceFieldKey: string = OrderPricing.MatrixItemFields.economy) {
        // Shorthand to return pricing matrix level 1, economyPricing value
        return OrderPricing.getOrderLevelPricing(pricingMatrix, priceLevel, priceFieldKey);
    }

    public static getOrderPricingLevel2Economy(pricingMatrix: Array<any>, priceLevel: number = 2, priceFieldKey: string = OrderPricing.MatrixItemFields.economy) {
        // Shorthand to return pricing matrix level 2, economyPricing value
        return OrderPricing.getOrderLevelPricing(pricingMatrix, priceLevel, priceFieldKey);
    }

    public static getOrderPricingLevel3Economy(pricingMatrix: Array<any>, priceLevel: number = 3, priceFieldKey: string = OrderPricing.MatrixItemFields.economy) {
        // Shorthand to return pricing matrix level 3, economyPricing value
        return OrderPricing.getOrderLevelPricing(pricingMatrix, priceLevel, priceFieldKey);
    }

    public static getOrderPricingLevel1Standard(pricingMatrix: Array<any>, priceLevel: number = 1, priceFieldKey: string = OrderPricing.MatrixItemFields.standard) {
        // Shorthand to return pricing matrix level 1, standardPricing value
        return OrderPricing.getOrderLevelPricing(pricingMatrix, priceLevel, priceFieldKey);
    }

    public static getOrderPricingLevel1Rush(pricingMatrix: Array<any>, priceLevel: number = 1, priceFieldKey: string = OrderPricing.MatrixItemFields.rush) {
        // Shorthand to return pricing matrix level 1, rushPricing value
        return OrderPricing.getOrderLevelPricing(pricingMatrix, priceLevel, priceFieldKey);
    }
}



// WEBPACK FOOTER //
// ./src/order/orderpricing.tsx